import classNames from 'classnames';
import React, { useState } from 'react';
import { moveColumn } from '../../../services/columnSettings/columnOrder';
import { TrackProperty } from '../../../services/columnSettings/playlistColumns';
import Dialog from '../dialog/dialog';
import s from './viewOptions.module.scss';

/**
 * iTunes' View Options: which columns a list shows, and in what order. A
 * dialog rather than the submenu it replaced, because a list of a dozen
 * checkboxes inside a right-click menu closed on every third click and did
 * not fit the phone.
 *
 * Pure: the columns and what to do about them come in as props, so the
 * dialog can be tested without a column-settings store behind it. Rows are
 * shown in the order the list uses; the arrows move one step and a row can
 * be dragged onto another, both of which hand the whole new order back.
 */

export interface ViewOptionColumn {
  key: TrackProperty;
  name: string;
  visible: boolean;
}

interface Props {
  columns: ViewOptionColumn[];
  onToggle(key: TrackProperty, visible: boolean): void;
  onReorder(order: TrackProperty[]): void;
  onReset(): void;
  onClose(): void;
}

export default function ViewOptionsDialog({
  columns,
  onToggle,
  onReorder,
  onReset,
  onClose,
}: Props) {
  const visibleCount = columns.filter((x) => x.visible).length;
  const order = columns.map((x) => x.key);

  const [dragging, setDragging] = useState<TrackProperty>();
  const [over, setOver] = useState<TrackProperty>();

  function move(key: TrackProperty, by: number) {
    onReorder(moveColumn(order, key, order.indexOf(key) + by));
  }

  function drop(target: TrackProperty) {
    if (dragging !== undefined && dragging !== target) {
      onReorder(moveColumn(order, dragging, order.indexOf(target)));
    }

    setDragging(undefined);
    setOver(undefined);
  }

  return (
    <Dialog
      title="View options"
      onClose={onClose}
      onSubmit={(ev) => {
        ev.preventDefault();
        onClose();
      }}
    >
      <p className={s.help}>
        The columns this list shows, in this order. Drag a row or use the arrows to reorder. Widths,
        hidden columns and the order are kept per list and per screen size.
      </p>
      <ul className={s.list}>
        {columns.map((column, index) => (
          <li
            key={column.key}
            className={classNames(s.row, {
              [s.dragging]: dragging === column.key,
              [s.dropTarget]: over === column.key && dragging !== column.key,
            })}
            draggable
            onDragStart={(ev) => {
              ev.dataTransfer.effectAllowed = 'move';
              ev.dataTransfer.setData('text/plain', column.key);
              setDragging(column.key);
            }}
            onDragEnter={(ev) => {
              ev.preventDefault();
              setOver(column.key);
            }}
            onDragOver={(ev) => {
              ev.preventDefault();
              ev.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(ev) => {
              ev.preventDefault();
              drop(column.key);
            }}
            onDragEnd={() => {
              setDragging(undefined);
              setOver(undefined);
            }}
          >
            <label className={s.label}>
              <input
                type="checkbox"
                className={s.checkbox}
                checked={column.visible}
                // The last column standing cannot go: a list with no
                // columns has no way back to this dialog but the keyboard.
                disabled={column.visible && visibleCount === 1}
                onChange={(ev) => onToggle(column.key, ev.currentTarget.checked)}
              />
              {column.name}
            </label>
            <span className={s.moveButtons}>
              <button
                type="button"
                className={s.moveButton}
                aria-label={`Move ${column.name} up`}
                disabled={index === 0}
                onClick={() => move(column.key, -1)}
              >
                ▲
              </button>
              <button
                type="button"
                className={s.moveButton}
                aria-label={`Move ${column.name} down`}
                disabled={index === columns.length - 1}
                onClick={() => move(column.key, 1)}
              >
                ▼
              </button>
            </span>
          </li>
        ))}
      </ul>
      <div className={s.actions}>
        <button type="button" className="button secondary" onClick={onReset}>
          Reset columns
        </button>
        <button type="submit" className="button">
          Close
        </button>
      </div>
    </Dialog>
  );
}
